import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Users,
  Lock,
  RefreshCw,
  Building2,
  Trash2,
  Shield,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { useBatchOperations } from '../../../hooks/platform-admin/useBatchOperations';
import { BatchOperationResult, BatchStatistics } from '../../../services/platform-admin/batch-operations.service';

export default function BatchOperationsPage() {
  const {
    getStatistics,
    batchPasswordReset,
    batchUpdateOrganizations,
    batchDeleteUsers,
    batchUpdateUsers,
    isLoading,
    error: hookError,
  } = useBatchOperations();

  const [searchParams, setSearchParams] = useSearchParams();

  const [stats, setStats] = useState<BatchStatistics | null>(null);

  // Get initial tab from URL or default to 'password'
  const urlTab = searchParams.get('tab');
  const initialTab = (urlTab === 'orgs' || urlTab === 'users' ? urlTab : 'password') as 'password' | 'orgs' | 'users';
  const [activeTab, setActiveTab] = useState<'password' | 'orgs' | 'users'>(initialTab);

  const [result, setResult] = useState<BatchOperationResult | null>(null);
  const [error, setError] = useState('');

  // Update URL when tab changes
  useEffect(() => {
    if (activeTab !== searchParams.get('tab')) {
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab, searchParams, setSearchParams]);

  // Update active tab when URL changes (e.g., browser back/forward)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl as 'password' | 'orgs' | 'users');
    }
  }, [searchParams, activeTab]);

  // Password Reset State
  const [passwordUserIds, setPasswordUserIds] = useState('');
  const [passwordOrgIds, setPasswordOrgIds] = useState('');
  const [passwordEmails, setPasswordEmails] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Organization Update State
  const [orgIds, setOrgIds] = useState('');
  const [orgStatus, setOrgStatus] = useState<string>('');
  const [orgPausedReason, setOrgPausedReason] = useState('');

  // User Delete State
  const [deleteUserIds, setDeleteUserIds] = useState('');
  const [deleteReason, setDeleteReason] = useState('');

  // User Update State
  const [updateUserIds, setUpdateUserIds] = useState('');
  const [updateRole, setUpdateRole] = useState<string>('');
  const [updateIsActive, setUpdateIsActive] = useState<string>('');

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    const result = await getStatistics();
    if (result) {
      setStats(result);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);

    if (!newPassword) {
      setError('New password is required');
      return;
    }

    const userIds = passwordUserIds
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id);
    const orgIds = passwordOrgIds
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id);
    const emails = passwordEmails
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email);

    if (userIds.length === 0 && orgIds.length === 0 && emails.length === 0) {
      setError('Please provide at least one: User IDs, Organization IDs, or Emails');
      return;
    }

    const res = await batchPasswordReset({
      userIds: userIds.length > 0 ? userIds : undefined,
      organizationIds: orgIds.length > 0 ? orgIds : undefined,
      emails: emails.length > 0 ? emails : undefined,
      newPassword,
    });

    if (res) {
      setResult(res);
      // Clear form on success
      if (res.success) {
        setPasswordUserIds('');
        setPasswordOrgIds('');
        setPasswordEmails('');
        setNewPassword('');
      }
    }
  };

  const handleUpdateOrganizations = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);

    const ids = orgIds
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id);

    if (ids.length === 0) {
      setError('Please provide at least one Organization ID');
      return;
    }

    if (!orgStatus) {
      setError('Please select a status');
      return;
    }

    const res = await batchUpdateOrganizations({
      organizationIds: ids,
      updates: {
        status: orgStatus as any,
        pausedReason: orgStatus === 'PAUSED' ? orgPausedReason : undefined,
      },
    });

    if (res) {
      setResult(res);
      if (res.success) {
        setOrgIds('');
        setOrgStatus('');
        setOrgPausedReason('');
      }
    }
  };

  const handleDeleteUsers = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);

    const ids = deleteUserIds
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id);

    if (ids.length === 0) {
      setError('Please provide at least one User ID');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${ids.length} user(s)? This cannot be undone.`)) {
      return;
    }

    const res = await batchDeleteUsers({
      userIds: ids,
      reason: deleteReason,
    });

    if (res) {
      setResult(res);
      if (res.success) {
        setDeleteUserIds('');
        setDeleteReason('');
      }
    }
  };

  const handleUpdateUsers = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);

    const ids = updateUserIds
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id);

    if (ids.length === 0) {
      setError('Please provide at least one User ID');
      return;
    }

    if (!updateRole && !updateIsActive) {
      setError('Please select at least one field to update');
      return;
    }

    const res = await batchUpdateUsers({
      userIds: ids,
      updates: {
        role: updateRole ? (updateRole as any) : undefined,
        isActive: updateIsActive ? updateIsActive === 'true' : undefined,
      },
    });

    if (res) {
      setResult(res);
      if (res.success) {
        setUpdateUserIds('');
        setUpdateRole('');
        setUpdateIsActive('');
      }
    }
  };

  const tabs = [
    { id: 'password', name: 'Password Reset', icon: Lock },
    { id: 'orgs', name: 'Update Organizations', icon: Building2 },
    { id: 'users', name: 'Manage Users', icon: Users },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Batch Operations</h1>
          <p className="text-sm text-gray-600 mt-1">
            Perform bulk operations on users and organizations
          </p>
        </div>
        <button
          onClick={loadStatistics}
          disabled={isLoading}
          className="flex items-center gap-2 bg-white text-gray-700 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="text-sm text-gray-600">Total Users</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.totalUsers}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="text-sm text-gray-600">Total Organizations</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.totalOrganizations}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="text-sm text-gray-600">Active Organizations</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{stats.activeOrganizations}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="text-sm text-gray-600">Paused Organizations</div>
            <div className="text-2xl font-bold text-yellow-600 mt-1">{stats.pausedOrganizations}</div>
          </div>
        </div>
      )}

      {/* Warning Banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800">Caution: Batch Operations</h3>
            <p className="text-sm text-yellow-700 mt-1">
              These operations affect multiple records at once and execute immediately. Please double-check
              all inputs before proceeding. Some operations cannot be undone.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id as any);
                  setResult(null);
                  setError('');
                }}
                className={`flex items-center gap-2 pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  isActive
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Error Message */}
      {(error || hookError) && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error || hookError}
        </div>
      )}

      {/* Result Display */}
      {result && (
        <div
          className={`border rounded-lg p-6 ${
            result.success
              ? 'bg-green-50 border-green-200'
              : 'bg-yellow-50 border-yellow-200'
          }`}
        >
          <div className="flex items-start gap-3 mb-4">
            {result.success ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : (
              <AlertTriangle className="h-6 w-6 text-yellow-600" />
            )}
            <div>
              <h3 className={`text-lg font-semibold ${result.success ? 'text-green-900' : 'text-yellow-900'}`}>
                {result.success ? 'Operation Completed Successfully' : 'Operation Completed with Errors'}
              </h3>
              <div className={`text-sm mt-2 ${result.success ? 'text-green-800' : 'text-yellow-800'}`}>
                <div>Total Processed: {result.totalProcessed}</div>
                <div>Successful: {result.successful}</div>
                {result.failed > 0 && <div>Failed: {result.failed}</div>}
              </div>
            </div>
          </div>

          {result.errors && result.errors.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-2">Errors:</h4>
              <div className="space-y-1">
                {result.errors.map((err, idx) => (
                  <div key={idx} className="text-sm text-red-700 bg-white rounded px-3 py-2 border border-red-200">
                    <span className="font-mono text-xs">{err.id}</span>: {err.error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        {activeTab === 'password' && (
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Batch Password Reset</h3>
              <p className="text-sm text-gray-600 mb-4">
                Reset passwords for multiple users. You can target users by User IDs, Organization IDs, or Email addresses.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User IDs (comma-separated)
              </label>
              <textarea
                value={passwordUserIds}
                onChange={(e) => setPasswordUserIds(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                placeholder="user-123, user-456, user-789"
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organization IDs (comma-separated)
              </label>
              <textarea
                value={passwordOrgIds}
                onChange={(e) => setPasswordOrgIds(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                placeholder="org-001, org-002"
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Addresses (comma-separated)
              </label>
              <textarea
                value={passwordEmails}
                onChange={(e) => setPasswordEmails(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                placeholder="user1@example.com, user2@example.com"
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password *
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Enter new password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all disabled:opacity-50 font-medium"
            >
              <Lock className="h-5 w-5" />
              {isLoading ? 'Processing...' : 'Reset Passwords'}
            </button>
          </form>
        )}

        {activeTab === 'orgs' && (
          <form onSubmit={handleUpdateOrganizations} className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Batch Update Organizations</h3>
              <p className="text-sm text-gray-600 mb-4">
                Update status and settings for multiple organizations at once.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organization IDs (comma-separated) *
              </label>
              <textarea
                value={orgIds}
                onChange={(e) => setOrgIds(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                placeholder="org-001, org-002, org-003"
                rows={3}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Status *
              </label>
              <select
                value={orgStatus}
                onChange={(e) => setOrgStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                required
              >
                <option value="">Select status...</option>
                <option value="ACTIVE">Active</option>
                <option value="PAUSED">Paused</option>
                <option value="TRIAL">Trial</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
            </div>

            {orgStatus === 'PAUSED' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pause Reason
                </label>
                <textarea
                  value={orgPausedReason}
                  onChange={(e) => setOrgPausedReason(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Why are these organizations being paused?"
                  rows={3}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all disabled:opacity-50 font-medium"
            >
              <Building2 className="h-5 w-5" />
              {isLoading ? 'Processing...' : 'Update Organizations'}
            </button>
          </form>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Update Users Form */}
            <form onSubmit={handleUpdateUsers} className="space-y-4 pb-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Update Users</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Update role or active status for multiple users.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User IDs (comma-separated) *
                </label>
                <textarea
                  value={updateUserIds}
                  onChange={(e) => setUpdateUserIds(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                  placeholder="user-123, user-456"
                  rows={2}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Role (optional)
                  </label>
                  <select
                    value={updateRole}
                    onChange={(e) => setUpdateRole(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Don't change</option>
                    <option value="ADMIN">Admin</option>
                    <option value="MANAGER">Manager</option>
                    <option value="SUPERVISOR">Supervisor</option>
                    <option value="AGENT">Agent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Active Status (optional)
                  </label>
                  <select
                    value={updateIsActive}
                    onChange={(e) => setUpdateIsActive(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Don't change</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 font-medium"
              >
                <Shield className="h-5 w-5" />
                {isLoading ? 'Processing...' : 'Update Users'}
              </button>
            </form>

            {/* Delete Users Form */}
            <form onSubmit={handleDeleteUsers} className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-red-900 mb-2 flex items-center gap-2">
                  <Trash2 className="h-5 w-5" />
                  Delete Users
                </h3>
                <p className="text-sm text-red-700 mb-4">
                  Permanently delete multiple users. This action cannot be undone.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User IDs (comma-separated) *
                </label>
                <textarea
                  value={deleteUserIds}
                  onChange={(e) => setDeleteUserIds(e.target.value)}
                  className="w-full px-4 py-2 border border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent font-mono text-sm"
                  placeholder="user-123, user-456"
                  rows={2}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deletion Reason
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Why are these users being deleted?"
                  rows={3}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all disabled:opacity-50 font-medium"
              >
                <Trash2 className="h-5 w-5" />
                {isLoading ? 'Processing...' : 'Delete Users'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
